import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const STORAGE_KEY = 'casaothers_cookie_consent';

// GA4 measurement ID used by Google's Consent Mode.
const GA_ID = 'G-K656E4E0L8';

const setConsent = (granted) => {
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: granted ? 'granted' : 'denied',
      ad_storage: granted ? 'granted' : 'denied',
    });
  }
  if (granted && typeof window.gtag === 'function') {
    window.gtag('event', 'cookie_consent_accepted', { send_to: GA_ID });
  }
};

const CookieBanner = () => {
  const { language } = useLanguage();
  const isNL = language === 'nl';
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      // Default consent state: denied until the user actively chooses.
      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'default', {
          analytics_storage: 'denied',
          ad_storage: 'denied',
          wait_for_update: 500,
        });
      }
      setVisible(true);
    } else if (stored === 'accepted') {
      setConsent(true);
    } else {
      setConsent(false);
    }

    const handler = () => setVisible(true);
    window.addEventListener('cookie-consent-reset', handler);
    return () => window.removeEventListener('cookie-consent-reset', handler);
  }, []);

  const accept = () => {
    localStorage.setItem(STORAGE_KEY, 'accepted');
    setConsent(true);
    setVisible(false);
  };

  const decline = () => {
    localStorage.setItem(STORAGE_KEY, 'declined');
    setConsent(false);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div style={styles.banner} role="dialog" aria-live="polite" data-testid="cookie-banner">
      <div style={styles.inner}>
        <div style={styles.textCol}>
          <h2 style={styles.title}>{isNL ? 'Cookies op CasaOthers' : 'Cookies on CasaOthers'}</h2>
          <p style={styles.text}>
            {isNL
              ? 'Wij gebruiken functionele en analytische cookies om de website te laten werken en het gebruik te begrijpen. Geanonimiseerde data helpt ons de site te verbeteren. Lees meer in ons '
              : 'We use functional and analytics cookies to make the website work and understand usage. Anonymised data helps us improve the site. Read more in our '}
            <Link to="/cookies" style={styles.link}>
              {isNL ? 'cookiebeleid' : 'cookies policy'}
            </Link>.
          </p>
        </div>
        <div style={styles.btnCol}>
          <button
            onClick={decline}
            style={styles.btnSecondary}
            data-testid="cookie-decline-button"
          >
            {isNL ? 'Alleen noodzakelijk' : 'Only necessary'}
          </button>
          <button
            onClick={accept}
            style={styles.btnPrimary}
            data-testid="cookie-accept-button"
          >
            {isNL ? 'Accepteer alle cookies' : 'Accept all cookies'}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  banner: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
    backgroundColor: '#1A1918',
    color: 'white',
    borderTop: '2px solid #C5A059',
    boxShadow: '0 -8px 32px rgba(0,0,0,0.25)',
  },
  inner: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '1.5rem',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '1.5rem',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  textCol: { flex: '1 1 380px' },
  title: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.1rem',
    margin: '0 0 0.4rem',
    color: 'white',
  },
  text: { fontSize: '0.875rem', margin: 0, lineHeight: 1.55, color: 'rgba(255,255,255,0.86)' },
  link: { color: '#C5A059', textDecoration: 'underline' },
  btnCol: {
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap',
  },
  btnPrimary: {
    backgroundColor: '#C5A059',
    color: 'white',
    border: 'none',
    padding: '0.75rem 1.25rem',
    fontSize: '0.85rem',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    fontWeight: 500,
  },
  btnSecondary: {
    backgroundColor: 'transparent',
    color: 'white',
    border: '1px solid rgba(255,255,255,0.5)',
    padding: '0.75rem 1.25rem',
    fontSize: '0.85rem',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    fontWeight: 500,
  },
};

export default CookieBanner;
